/**
 * useChainSession — manages the on-demand BSC WebSocket session.
 *
 * • Activates the session on mount (POST /api/wallet/chain/session).
 * • Deactivates it on unmount (DELETE /api/wallet/chain/session).
 * • Polls /api/wallet/chain/status every 5 s to refresh secondsRemaining.
 * • Returns { active, secondsRemaining, ttl, expired, refresh }.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const POLL_MS = 5000;

export default function useChainSession() {
  const [session, setSession] = useState({
    active: false,
    secondsRemaining: 0,
    ttl: 300,
    expired: false,
  });
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/api/wallet/chain/status");
      const sess = data?.session || {};
      if (mountedRef.current) {
        setSession((prev) => ({
          active: !!sess.active,
          secondsRemaining: sess.seconds_remaining ?? 0,
          ttl: sess.ttl ?? 300,
          // Mark expired only once — stays expired until user clicks refresh
          expired: prev.expired || (!sess.active && prev.active),
        }));
      }
    } catch {
      // ignore polling errors
    }
  }, []);

  // Activate on mount
  useEffect(() => {
    mountedRef.current = true;

    const activate = async () => {
      try {
        const { data } = await api.post("/api/wallet/chain/session");
        const sess = data?.session || {};
        if (mountedRef.current) {
          setSession({
            active: !!sess.active,
            secondsRemaining: sess.seconds_remaining ?? 0,
            ttl: sess.ttl ?? 300,
            expired: false,
          });
        }
      } catch {
        // Session activation failed (e.g. user logged out) — ignore
      }
    };

    activate();
    intervalRef.current = setInterval(fetchStatus, POLL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
      // Fire-and-forget deactivate on unmount
      api.delete("/api/wallet/chain/session").catch(() => {});
    };
  }, [fetchStatus]);

  // Manual refresh — re-activate and reset expired flag
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.post("/api/wallet/chain/session");
      const sess = data?.session || {};
      setSession({
        active: !!sess.active,
        secondsRemaining: sess.seconds_remaining ?? 0,
        ttl: sess.ttl ?? 300,
        expired: false,
      });
    } catch {
      // ignore
    }
  }, []);

  return { ...session, refresh };
}
